import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const WARN_BEFORE_MS = 5 * 60 * 1000; // warn 5 minutes before expiry
const POLL_INTERVAL_MS = 30 * 1000;   // poll every 30s until warning window

function formatTimeLeft(ms) {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins > 0) {
        return `${mins}m ${secs.toString().padStart(2, "0")}s`;
    }
    return `${secs}s`;
}

/**
 * Returns the formatted countdown string (e.g. "4m 32s") when the session
 * is within 5 minutes of expiry. Returns "" otherwise.
 * Call from the Nav component to show the countdown in the profile label.
 *
 * Uses a 30-second poll instead of a single long setTimeout so the warning
 * still fires reliably after laptop sleep, tab throttling, etc.
 */
const useSessionExpiryWarning = () => {
    const refreshExpiresAt = useSelector((s) => s.auth.refreshExpiresAt);
    const intervalRef = useRef(null);
    const freqRef = useRef(null);
    const [countdown, setCountdown] = useState("");

    useEffect(() => {
        // Clear any previous interval whenever expiry changes
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        freqRef.current = null;
        setCountdown("");

        if (!refreshExpiresAt) return;

        const expiryMs = new Date(refreshExpiresAt).getTime();
        if (isNaN(expiryMs)) return;

        const tick = () => {
            const msLeft = expiryMs - Date.now();

            if (msLeft <= 0) {
                // Session expired — stop ticking, show expired label
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                freqRef.current = null;
                setCountdown("Session Expired");
                return;
            }

            if (msLeft <= WARN_BEFORE_MS) {
                // Inside warning window — show countdown, switch to 1s ticks
                setCountdown(formatTimeLeft(msLeft));
                if (freqRef.current !== 1000) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = setInterval(tick, 1000);
                    freqRef.current = 1000;
                }
            }
            // else: not yet in warning window — keep polling at 30s
        };

        // Run first check immediately
        tick();

        // If tick() didn't already start the 1s countdown, start the 30s poll
        if (!intervalRef.current) {
            intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);
            freqRef.current = POLL_INTERVAL_MS;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            freqRef.current = null;
        };
    }, [refreshExpiresAt]);

    return countdown;
};

export default useSessionExpiryWarning;
